/**
 * SO-3c — Spec sheets on the party's own link.
 *
 *   /site-office/:token/specs            the list: category chips, rooms, rows
 *   /site-office/:token/specs/:clipId    one sheet (SiteOfficeSpecItem)
 *
 * The list reads GET /api/site-office-links/{token}/specs, which is filtered by
 * the same helper the home card's counts use. Rows are the SO-3a row pattern.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { cacheBranding } from '@/utils/skin';
import OfficeStrip, { parsePreview, withPreview } from '@/components/site-office/OfficeStrip';
import '@/styles/site-office.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const bare = axios.create();

export const abs = (u) => (u && u.startsWith('/api') ? `${API}${u.replace(/^\/api/, '')}` : u);

export const sheetName = (row) => row.tag_name || row.title || 'Spec sheet';

/** One spec row: the picture, the name, where it goes, and the way in. */
export const SpecRow = ({ token, row, cat, preview }) => (
  <Link className="kso-row link" data-testid={`site-office-spec-row-${row.clip_id}`}
    to={withPreview(`/site-office/${token}/specs/${row.clip_id}${cat ? `?cat=${cat}` : ''}`, preview)}>
    {row.thumb ? <img className="th" src={abs(row.thumb)} alt="" loading="lazy" />
      : <span className="th" />}
    <div className="t">
      <b>{sheetName(row)}</b>
      <small>{[row.spec_num, row.room_location].filter(Boolean).join(' · ')}</small>
    </div>
    <span className="chev">›</span>
  </Link>
);

export const SiteOfficeHead = ({ data }) => (
  <header className="kso-head">
    {data.branding?.logo_url ? <img className="kso-head__logo" src={data.branding.logo_url} alt="" /> : null}
    <div className="kso-head__k"><b>{data.firm_name}</b> · Site Office</div>
    <div className="kso-head__who">
      <div className="n">{data.party?.name}</div>
      <div className="r">{data.project?.name}</div>
    </div>
  </header>
);

export default function SiteOfficeSpecs() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [office, setOffice] = useState(null);
  const [specs, setSpecs] = useState(null);
  const [gone, setGone] = useState('');
  const cat = params.get('cat') || '';
  // SO-9a2 — the firm reading this page from the preview: the strip travels.
  const preview = parsePreview(params.get('preview'));

  const load = useCallback(async () => {
    try {
      const [head, list] = await Promise.all([
        bare.get(`${API}/site-office-links/${token}`),
        bare.get(`${API}/site-office-links/${token}/specs`),
      ]);
      setOffice(head.data);
      if (head.data?.branding) cacheBranding(head.data.branding);
      setSpecs(list.data);
    } catch (_e) { setGone('This link is not valid'); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (gone) {
    return (
      <main className="kso-page" data-testid="site-office-specs">
        <div className="kso-wrap"><div className="kso-gate"><h1>{gone}</h1></div></div>
      </main>
    );
  }
  if (!office || !specs) {
    return (
      <main className="kso-page" data-testid="site-office-specs">
        <div className="kso-wrap"><div className="kso-gate"><p>Opening…</p></div></div>
      </main>
    );
  }

  const pick = (code) => {
    const next = new URLSearchParams(params);
    if (code) next.set('cat', code); else next.delete('cat');
    setParams(next, { replace: true });
  };

  const rows = (specs.rows || []).filter((r) => !cat || r.spec_category === cat);
  const rooms = [];
  rows.forEach((row) => {
    const room = row.room || 'Not in a room yet';
    const last = rooms[rooms.length - 1];
    if (last && last.room === room) last.rows.push(row);
    else rooms.push({ room, rows: [row] });
  });

  return (
    <main className="kso-page" data-testid="site-office-specs">
      <div className="kso-wrap">
        {preview ? (
          <OfficeStrip projectId={preview.projectId} membershipId={preview.membershipId}
            projectName={office.project?.name} partyName={office.party?.name} page="Spec sheets" />
        ) : null}
        <SiteOfficeHead data={office} />
        <div className="kso-crumb" data-testid="site-office-specs-crumb">
          <button type="button" className="back" data-testid="site-office-specs-back"
            onClick={() => navigate(preview ? `/projects/${preview.projectId}/site-office/${preview.membershipId}?view=party` : `/site-office/${token}`)}>‹ Site Office</button>
          <span>Site Office</span><span>›</span><b>Spec sheets</b>
        </div>
        <div className="kso-grid">
          <section className="kso-card w12" data-testid="site-office-specs-card">
            <div className="kso-card__hd">
              Spec sheets for your work
              <span className="n">{rows.length} of {(specs.rows || []).length}</span>
            </div>
            <div className="kso-card__bd">
              <div className="kso-visits" data-testid="site-office-specs-cats">
                <span className={cat ? undefined : 'on'} onClick={() => pick('')}
                  data-testid="site-office-spec-cat-all">All</span>
                {(specs.categories || []).map((c) => (
                  <span key={c.code} className={cat === c.code ? 'on' : undefined}
                    title={c.name} onClick={() => pick(c.code)}
                    data-testid={`site-office-spec-cat-${c.code}`}>
                    {c.code} · {c.count}
                  </span>
                ))}
              </div>
              {rooms.map((group) => (
                <div key={group.room} data-testid={`site-office-spec-room-${group.room.replace(/\s+/g, '-')}`}>
                  <div className="ktm-group">{group.room}</div>
                  {group.rows.map((row) => <SpecRow key={row.clip_id} token={token} row={row} cat={cat} preview={preview} />)}
                </div>
              ))}
              {rows.length === 0 ? <p className="kso-hint">Nothing in this category.</p> : null}
            </div>
          </section>
        </div>
        <div className="kso-foot">
          {office.firm_name} · this link is personal to {office.party?.name}
        </div>
      </div>
    </main>
  );
}
